import { ApiError, buildQuery, type ApiClient, type Downloaded, type HttpMethod, type Query, type RequestOptions } from '@saas/ui';
import { tenantStore, tokenStore } from './tokens.js';

/**
 * fetch ラッパー。
 *   - base '/api'（Vite の dev proxy → http://localhost:4000）。JSON。Authorization: Bearer
 *   - 401 → POST /auth/refresh を 1 回だけ（同時多発は single-flight）→ 元の要求を再試行
 *   - リフレッシュも失敗 → トークン破棄 → onUnauthenticated（ログインへ）
 *   - サブドメインが無い環境では X-Tenant: <slug>
 *   - エラーは { error: { code, message, details } } → ApiError に正規化。到達不能は status 0
 */
export interface ClientConfig {
  baseUrl?: string;
  onUnauthenticated?: () => void;
}

const AUTH_FREE = ['/auth/login', '/auth/signup', '/auth/refresh', '/auth/logout', '/auth/tenant'];

export function createApiClient(cfg: ClientConfig = {}): ApiClient & { setUnauthenticatedHandler(fn: () => void): void; refreshTokens(): Promise<boolean> } {
  const baseUrl = cfg.baseUrl ?? '/api';
  let onUnauthenticated = cfg.onUnauthenticated ?? (() => {});
  let refreshing: Promise<boolean> | null = null;

  async function rotateRefreshToken(): Promise<boolean> {
      const refreshToken = tokenStore.getRefresh();
      if (!refreshToken) return false;
      try {
        const res = await fetch(`${baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) tokenStore.clear();
          return false;
        }
        const data = (await res.json()) as { accessToken: string; refreshToken: string };
        tokenStore.setAccess(data.accessToken);
        tokenStore.setRefresh(data.refreshToken);
        return true;
      } catch {
        return false;
      }
  }

  async function refreshTokens(): Promise<boolean> {
    if (refreshing) return refreshing;
    refreshing = (async () => {
      // localStorage is shared by tabs, but an in-memory single-flight is not. Serializing rotation
      // prevents two tabs from presenting the same token and triggering refresh-token reuse defence.
      // The token is deliberately read inside the lock so a waiting tab uses the newest rotated value.
      if (typeof navigator !== 'undefined' && navigator.locks) {
        return navigator.locks.request('saas-auth-refresh', { mode: 'exclusive' }, () => rotateRefreshToken());
      }
      return rotateRefreshToken();
    })().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  function tenantHeaders(): Record<string, string> {
    if (!tenantStore.needsHeader()) return {};
    const slug = tenantStore.stored();
    return slug ? { 'X-Tenant': slug } : {};
  }

  function headersFor(opts: RequestOptions, hasBody: boolean): Record<string, string> {
    const h: Record<string, string> = { Accept: 'application/json', ...tenantHeaders(), ...(opts.headers ?? {}) };
    if (hasBody) h['Content-Type'] = 'application/json';
    const token = tokenStore.getAccess();
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }

  async function toError(res: Response): Promise<ApiError> {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* not json */
    }
    const err = (body as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    if (err && typeof err.message === 'string') return new ApiError(res.status, err.code ?? 'HTTP', err.message, err.details ?? null);
    const fallback =
      res.status === 401 ? 'もう一度ログインしてください' : res.status === 403 ? 'この操作を行う権限がありません' : res.status === 404 ? '対象が見つかりません' : res.status >= 500 ? 'サーバ内部でエラーが発生しました' : `要求に失敗しました（${res.status}）`;
    return new ApiError(res.status, res.status === 404 ? 'NOT_FOUND' : 'HTTP', fallback);
  }

  async function doFetch(method: HttpMethod, path: string, opts: RequestOptions, retried: boolean): Promise<Response> {
    const hasBody = opts.body !== undefined && method !== 'GET';
    let res: Response;
    try {
      res = await fetch(`${baseUrl}${path}${buildQuery(opts.query)}`, {
        method,
        headers: headersFor(opts, hasBody),
        body: hasBody ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
        credentials: 'same-origin',
        ...(opts.keepalive ? { keepalive: true } : {}),
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw ApiError.network();
    }
    const authFree = opts.skipAuthRefresh || AUTH_FREE.some((p) => path.startsWith(p));
    if (res.status === 401 && !authFree && !retried) {
      const ok = await refreshTokens();
      if (ok) return doFetch(method, path, opts, true);
      tokenStore.clear();
      onUnauthenticated();
    }
    return res;
  }

  async function request<T>(method: HttpMethod, path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await doFetch(method, path, opts, false);
    if (!res.ok) throw await toError(res);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  function filenameFrom(res: Response): string | null {
    const cd = res.headers.get('content-disposition');
    if (!cd) return null;
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    if (star?.[1]) {
      try {
        return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
      } catch {
        /* fallthrough */
      }
    }
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    return plain?.[1]?.trim() ?? null;
  }

  const client = {
    request,
    get: <T,>(path: string, query?: Query, opts: Omit<RequestOptions, 'query' | 'body'> = {}) => request<T>('GET', path, { ...opts, query }),
    post: <T,>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) => request<T>('POST', path, { ...opts, body: body ?? {} }),
    patch: <T,>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) => request<T>('PATCH', path, { ...opts, body: body ?? {} }),
    put: <T,>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) => request<T>('PUT', path, { ...opts, body: body ?? {} }),
    del: <T,>(path: string, opts: RequestOptions = {}) => request<T>('DELETE', path, opts),
    download: async (path: string, query?: Query): Promise<Downloaded> => {
      const res = await doFetch('GET', path, { query, headers: { Accept: '*/*' } }, false);
      if (!res.ok) throw await toError(res);
      return { blob: await res.blob(), filename: filenameFrom(res) };
    },
    setUnauthenticatedHandler: (fn: () => void) => {
      onUnauthenticated = fn;
    },
    refreshTokens,
  };
  return client;
}

/** アプリ全体で 1 つ */
export const api = createApiClient();
